import Button from 'components/ui/Button';
import Head from 'next/head';
import Icons from 'components/icons';
import Position from 'components/ui/Position';
import RoomButton from 'components/ui/RoomButton';
import { imageUrl, sanityFetch } from 'api';
import { RoomUtil } from 'utils';
import type { GetStaticProps, NextPage } from 'next';
import type { General, RoomTypeLink } from 'type';
import enabledLanguages from '../../../enabledLanguages';
import GeneralInfo from 'compositions/GeneralInfo';
import LanguageSelector from 'compositions/LanguageSelector';
import { useAppDispatch } from 'store/hooks';
import { useEffect } from 'react';
import { setGeneral } from 'store/generalSlice';
import useScreenSizeGuard from 'hooks/useScreenSizeGuard';
import generalData from 'translations/general.json';

type RoomPageProps = {
  roomTypeLinks: RoomTypeLink[];
  locale: string;
  // general: General;
};

const createRoomUrl = (roomTypeLink: RoomTypeLink) =>
  `/room/${roomTypeLink.slugName}`;

const RoomPage: NextPage<RoomPageProps> = ({ roomTypeLinks, locale }) => {
  useScreenSizeGuard();
  const dispatch = useAppDispatch();

  const lang = (locale || 'en') as any;

  const localeGeneralData = generalData as any;

  const general = {
    ...localeGeneralData[lang],
  } as General;

  useEffect(() => {
    dispatch(setGeneral(general));
  }, [general, dispatch]);

  return (
    <>
      <Head>
        {general?.seo?.title && <title>{general.seo.title}</title>}
        {general?.seo?.description && (
          <meta name="description" content={general.seo.description} />
        )}
        {general?.seo?.shareImage && (
          <meta
            property="og:image"
            content={
              general.seo.shareImage.localMedia
                ? general.seo.shareImage.localMedia
                : imageUrl(general.seo.shareImage).toString()
            }
          />
        )}
      </Head>

      <GeneralInfo
        noGrid
        title={general.home.title}
        subtitle={general.newRecipe?.title}
        text={general.newRecipe?.preamble || []}
      >
        <div className="hidden supported:grid w-full gap-2 grid-cols-2 mb-20">
          {roomTypeLinks
            .filter(RoomUtil.isRoomTypeValid)
            .map((roomTypeLink) => (
              <RoomButton
                key={roomTypeLink._id}
                url={createRoomUrl(roomTypeLink)}
                title={roomTypeLink.title}
              />
            ))}
        </div>
      </GeneralInfo>

      <LanguageSelector />

      <Position position="topLeft">
        <Button iconOnly url="/" color="blue">
          <Icons.Home className="!h-6" />
        </Button>
      </Position>
    </>
  );
};

export const getStaticProps: GetStaticProps<RoomPageProps> = async (
  context,
) => {
  const lang = context.locale || 'en';
  const query =
    '*[_type == "roomConfig"]{_id,title,"slugName":slug.current,"orderRank":settings->orderRank,"lang":__i18n_lang}';
  const rooms = await sanityFetch<RoomTypeLink[]>(query);
  const roomTypeLinks = rooms
    .filter((room) => enabledLanguages.short.includes(room.lang))
    .filter(RoomUtil.isRoomTypeValid)
    .filter((room) => room.lang === lang)
    .sort((a, b) => b.orderRank - a.orderRank);

  const generalQuery = `*[_type == "general" && __i18n_lang == "${lang}"]{...,home{...,"roomLink":roomLink->slug.current}}`;
  // const generalQuery = `*[_type == "general" && __i18n_lang == "${lang}"]{...,home{...}}`;
  const [general] = await sanityFetch<General[]>(generalQuery);

  // console.log('GENERAL', JSON.stringify(general));

  return {
    props: { roomTypeLinks, locale: lang, general },
  };
};

export default RoomPage;
